import React, { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';

const FirstPartyDataMainController = (props) => {
  const {
    hashedEmailLists,
    loadingHashedEmailLists,
    refetchHashedEmailLists,
    refetchHashedEmailList,
    processDeviceIds,
    updateHashedEmailLatestStatus,
    currentOrg,
    searchTerm,
    setSearchTerm,
    selectedAdvertiserId,
    setSelectedAdvertiserId,
    providersVisible,
  } = props;

  const [allAdvertisers, setAllAdvertisers] = useState([]);

  // Extract unique advertisers from the data
  const hashedEmailListExtracted = useMemo(() => {
    return !Array.isArray(hashedEmailLists) &&
      hashedEmailLists &&
      hashedEmailLists.getHashedEmailListWithCount?.hashedEmailList
      ? hashedEmailLists.getHashedEmailListWithCount.hashedEmailList
      : [];
  }, [hashedEmailLists]);

  // Extract and cache all unique advertisers when data loads (only when no filter is applied)
  useEffect(() => {
    if (!selectedAdvertiserId && hashedEmailListExtracted.length > 0) {
      const advertisersMap = new Map();
      
      hashedEmailListExtracted.forEach(item => {
        if (item.orgs && item.orgs.length > 0) {
          const org = item.orgs[0];
          if (!advertisersMap.has(org.id)) {
            advertisersMap.set(org.id, {
              id: org.id,
              name: org.name
            });
          }
        }
      });
      
      const uniqueAdvertisers = Array.from(advertisersMap.values()).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      
      // Only update if the list has changed
      if (uniqueAdvertisers.length > 0 && JSON.stringify(allAdvertisers) !== JSON.stringify(uniqueAdvertisers)) {
        setAllAdvertisers(uniqueAdvertisers);
      }
    }
  }, [hashedEmailListExtracted, selectedAdvertiserId, allAdvertisers]);

  const activateHashedEmailList = async (data) => {
    return props.activateHashedEmailList({
      variables: {
        orgID: currentOrg?.id,
        hashedEmailListIds: Array.isArray(data.hashedEmailListIds) ? data.hashedEmailListIds : [data.emailListId],
        dsp: data.dsp ? data.dsp.toUpperCase() : data.dsp,
      },
    });
  };

  const createHashedEmailList = async (values) => {
    return props.createHashedEmailList({
      variables: {
        name: values.name,
        typeOfFirstPartyData: values.typeOfFirstPartyData,
        hashedEmailIds: values.hashedEmailIds,
        orgID: currentOrg?.id,
        stepProgress: values.stepProgress,
        status: values.status,
      },
    });
  };

  const updateHashedEmailList = async (values) => {
    return props.updateHashedEmailList({
      variables: {
        id: values.id,
        activated: values.activated,
        stepProgress: values.stepProgress,
        status: values.status,
        dsp: values.dsp,
        tag: values.tag,
      },
    });
  };

  const bulkCreateHashedEmailAddress = async (data) => {
    return props.createHashedEmailAddress({
      variables: {
        emailListId: data.emailListId,
        emails: data.emails,
      },
    });
  };

  const deleteHashedEmailList = async (record) => {
    await props.deleteHashedEmailList({
      variables: { id: record.id },
    });
    await props.deleteManyHashedEmailAddresses({
      variables: { id: record.id },
    });
  };

  const refetchHashedEmailListWithSecurityCheck = async (values) => {
    const response = await refetchHashedEmailList(values);
    
    // Security check: verify audience belongs to current organization
    if (response && response.data && response.data.hashedEmailLists && response.data.hashedEmailLists.length > 0) {
      const hashedEmailList = response.data.hashedEmailLists[0];
      
      if (hashedEmailList.orgs && hashedEmailList.orgs.length > 0) {
        const audienceOrg = hashedEmailList.orgs[0];

        // if the currentOrg and audienceOrg.id are the same, return the response
        if (currentOrg && audienceOrg && (audienceOrg.id === currentOrg.id)) {
          return response;
        }
        
        // Check if currentOrg.id exists in the parentOrg array
        let isAuthorized = false;
        
        if (Array.isArray(audienceOrg.parentOrg) && audienceOrg.parentOrg.length > 0) {
          // Check if currentOrg.id exists in any of the parentOrg items
          isAuthorized = audienceOrg.parentOrg.some(parentOrg => parentOrg.id === currentOrg.id);
        } else if (audienceOrg.parentOrg?.id) {
          // Handle parentOrg as single object
          isAuthorized = audienceOrg.parentOrg.id === currentOrg.id;
        } else if (audienceOrg.parentOrgId) {
          // Handle parentOrgId as direct property
          isAuthorized = audienceOrg.parentOrgId === currentOrg.id;
        }
        
        if (!isAuthorized) {
          // Return error response to trigger redirect in the component
          return { 
            unauthorized: true,
            message: 'You do not have permission to view this audience.'
          };
        }
      }
    }
    
    return response;
  };

  return React.cloneElement(props.children, {
    ...props.children.props,
    activateHashedEmailList,
    createHashedEmailList,
    updateHashedEmailList,
    bulkCreateHashedEmailAddress,
    hashedEmailLists,
    loadingHashedEmailLists,
    refetchHashedEmailLists,
    deleteHashedEmailList,
    processDeviceIds,
    refetchHashedEmailList: refetchHashedEmailListWithSecurityCheck,
    updateHashedEmailLatestStatus,
    currentOrg,
    searchTerm,
    setSearchTerm,
    selectedAdvertiserId,
    setSelectedAdvertiserId,
    allAdvertisers,
    providersVisible,
  });
};

export default FirstPartyDataMainController;
